import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { Menu, X } from "lucide-react";

const NAV_LINKS = [
  { href: "/", label: "Home" },
  { href: "/about", label: "About Us" },
  {
    label: "Services",
    children: [
      { href: "/services/retail", label: "Retail & Commercial" },
      { href: "/services/residential", label: "Residential & Mixed-Use" },
      { href: "/services/condo", label: "Condominium & Co-Op" },
    ],
  },
  { href: "/properties", label: "Properties" },
  { href: "/contact", label: "Contact Us" },
];

export default function SiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [location] = useLocation();

  useEffect(() => {
    let animationFrame: number | null = null;
    const onScroll = () => {
      if (animationFrame !== null) return;
      animationFrame = window.requestAnimationFrame(() => {
        const nextScrolled = window.scrollY > 10;
        setScrolled(current => current === nextScrolled ? current : nextScrolled);
        animationFrame = null;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      if (animationFrame !== null) window.cancelAnimationFrame(animationFrame);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  useEffect(() => { setMenuOpen(false); setServicesOpen(false); }, [location]);

  return (
    <header className="fixed top-0 inset-x-0 z-50">
    {/* Top utility bar */}
    <div className="bg-[#333338] text-[#D9D8DA] hidden md:block" style={{ fontSize: "13px", padding: "8px 0" }}>
      <div className="container flex items-center justify-end gap-6">
        <span>Let's Talk! <a href="tel:2129189037" className="font-bold" style={{ color: "#F0A03C" }}>(212) 918-9037</a></span>
        <span style={{ color: "#5A5A60" }}>|</span>
        <a href="mailto:contact@fcmre.com" className="hover:text-white transition-colors">contact@fcmre.com</a>
        <span style={{ color: "#5A5A60" }}>|</span>
        <a href="https://fcmre.unitconnect.com/tenant/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Tenant Login</a>
      </div>
    </div>

      {/* Main nav */}
    <div className={`bg-white border-b border-[#ECE8E2] transition-shadow duration-200 ${scrolled ? "shadow-md" : ""}`}>
      <div className="container flex items-center justify-between" style={{ padding: "14px 32px" }}>
        {/* Logo */}
        <Link href="/" className="flex items-center shrink-0">
          <img
            src="https://storage.googleapis.com/fcmre-media/assets/fcmre-logo.png"
            alt="FCMRE"
            width={185}
            height={52}
            className="w-auto"
            style={{ height: "52px" }}
          />
        </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center text-gray-700" style={{ gap: "24px" }}>
            {NAV_LINKS.map((link) =>
              link.children ? (
                <div key={link.label} className="relative">
                  <button
                    className={`flex items-center gap-1 transition-colors hover:text-[#E8542A] whitespace-nowrap ${location.startsWith("/services") ? "text-[#E8542A]" : ""}`}
                    style={{ fontWeight: 700, fontSize: "15px", padding: "8px 0" }}
                    onMouseEnter={() => setServicesOpen(true)}
                    onMouseLeave={() => setServicesOpen(false)}
                  >
                    {link.label}
                    <svg className={`w-3 h-3 transition-transform ${servicesOpen ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                  </button>
                  <div
                    onMouseEnter={() => setServicesOpen(true)}
                    onMouseLeave={() => setServicesOpen(false)}
                    className={`absolute top-full left-1/2 -translate-x-1/2 mt-1 w-56 bg-white border border-gray-100 shadow-lg overflow-hidden transition-all duration-150 origin-top ${servicesOpen ? "opacity-100 scale-100 pointer-events-auto" : "opacity-0 scale-95 pointer-events-none"}`}
                  >
                    {link.children.map((child) => (
                      <Link
                        key={child.href}
                        href={child.href}
                        className={`block px-4 py-3 text-sm hover:bg-gray-50 hover:text-[#E8542A] transition-colors border-b border-gray-50 last:border-0 ${location === child.href ? "text-[#E8542A]" : "text-gray-700"}`}
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <Link
                  key={link.href}
                  href={link.href!}
                  className={`transition-colors whitespace-nowrap ${location === link.href ? "text-[#E8542A]" : "hover:text-[#E8542A]"}`}
                  style={{ fontWeight: 700, fontSize: "15px", padding: "8px 0" }}
                >
                  {link.label}
                </Link>
              )
            )}
          </nav>

          {/* CTA */}
        <div className="hidden lg:flex items-center">
          <Link
            href="/contact"
            className="bg-[#E8542A] hover:bg-[#C9421D] text-white font-extrabold uppercase transition-colors active:scale-95 whitespace-nowrap"
            style={{ borderRadius: "2px", fontSize: "14px", letterSpacing: "0.02em", padding: "13px 24px" }}
          >
            REQUEST PROPOSAL
            </Link>
          </div>

          {/* Mobile hamburger */}
          <button
            className="lg:hidden text-gray-700 p-2"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100 shadow-lg">
          <nav className="container py-4 flex flex-col gap-1">
            {NAV_LINKS.map((link) =>
              link.children ? (
                <div key={link.label}>
                  <div className="text-gray-400 text-xs uppercase tracking-widest px-2 pt-3 pb-1">{link.label}</div>
                  {link.children.map((child) => (
                    <Link key={child.href} href={child.href} className={`block px-2 py-2.5 text-sm ${location === child.href ? "text-[#E8542A]" : "text-gray-700"}`}>
                      {child.label}
                    </Link>
                  ))}
                </div>
              ) : (
                <Link key={link.href} href={link.href!} className={`block px-2 py-2.5 text-sm ${location === link.href ? "text-[#E8542A]" : "text-gray-700"}`}>
                  {link.label}
                </Link>
              )
            )}
            <Link href="/contact" className="mt-3 bg-[#E8542A] text-white text-sm font-bold px-4 py-3 text-center uppercase tracking-wide">
              REQUEST PROPOSAL
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
